import {
  PERSPECTIVE_ADDRESSES as EULER_PERSPECTIVE_ADDRESSES,
  E_LEND_CHAIN_ID,
} from '@/config/euler-contracts'
import { INDEXER_GC_TIME, INDEXER_REFETCH_INTERVAL, INDEXER_STALE_TIME } from '@/config/consts'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, getAddress } from 'viem'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'
import type { EulerIndexerELend, EulerIndexerELendsResponse, NormalizedELendInfo } from './types'

// Re-export perspective addresses for backward compatibility
export const PERSPECTIVE_ADDRESSES = EULER_PERSPECTIVE_ADDRESSES[E_LEND_CHAIN_ID]!

// Default enabled perspectives (matching Euler UI defaults)
// Note: escrowedCollateral removed - escrow vaults are internal to Earn vaults
// and should not be shown to users for direct deposit
export const DEFAULT_ENABLED_PERSPECTIVES = [PERSPECTIVE_ADDRESSES.governed, PERSPECTIVE_ADDRESSES.edgeFactory]

// Default disabled perspectives (unverified vaults - hidden by default)
export const DEFAULT_DISABLED_PERSPECTIVES = [PERSPECTIVE_ADDRESSES.evkFactory, PERSPECTIVE_ADDRESSES.eulerEarnFactory]

/**
 * Settings for perspective filtering
 */
export interface PerspectiveSettings {
  enabledPerspectives?: string[]
  disabledPerspectives?: string[]
}

/**
 * Request body for the v2 vault list POST endpoint
 */
export interface VaultListRequestBody {
  chainId: number
  search?: string
  assets?: string[]
  inWallet?: Record<string, string>
  vaults?: string[]
  products?: string[]
  entities?: string[]
  settings?: {
    disableIntrinsicApy?: boolean
    disableRewardsApy?: boolean
    disabledPerspectives?: string[]
    enabledPerspectives?: string[]
  }
  utilizationMin?: string
  utilizationMax?: string
  orderBy?: 'vault' | 'asset' | 'assetSymbol' | 'apy' | 'utilization' | 'inWallet' | 'totalSupply'
  orderDirection?: 'asc' | 'desc'
  onlyInWallet?: boolean
  page?: string
  limit?: string
}

/**
 * Query key for Euler Indexer ELends (v2 vault list)
 */
export function eulerELendsQueryKey(
  chainId: number,
  perspectiveSettings?: PerspectiveSettings,
): readonly [string, string, number, PerspectiveSettings | undefined] {
  return ['euler', 'elends', chainId, perspectiveSettings] as const
}

/**
 * Fetch ELend vaults from Euler Indexer v2 API using POST
 */
async function fetchEulerIndexerELends(
  chainId: number,
  perspectiveSettings?: PerspectiveSettings,
): Promise<EulerIndexerELend[]> {
  const url = publicEulerRoutes.vaultList.clientUrl(chainId)

  const body: VaultListRequestBody = {
    chainId,
    limit: '100', // API max is 100
    page: '1',
    orderBy: 'totalSupply',
    orderDirection: 'desc',
    onlyInWallet: false,
    settings: {
      disableIntrinsicApy: false,
      disableRewardsApy: false,
      enabledPerspectives: perspectiveSettings?.enabledPerspectives,
      disabledPerspectives: perspectiveSettings?.disabledPerspectives,
    },
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch ELends from Euler Indexer: ${response.status} ${response.statusText}`)
  }

  const data: EulerIndexerELendsResponse = await response.json()

  if (!data || !Array.isArray(data.items)) {
    throw new Error('Invalid response format from Euler Indexer')
  }

  return data.items
}

/**
 * Transform Euler Indexer ELends to normalized format
 */
function normalizeELends(vaults: EulerIndexerELend[], chainId: number): Map<Address, NormalizedELendInfo> {
  const vaultMap = new Map<Address, NormalizedELendInfo>()

  for (const vault of vaults) {
    try {
      const normalizedAddress = getAddress(vault.vault) as Address
      const totalAssets = BigInt(vault.totalAssets || '0')
      const assetDecimals = vault.assetDecimals ?? 18

      // Calculate totalAssetsUSD from indexer price, fallback if needed
      let totalAssetsUSD = vault.totalAssetsUSD ?? 0
      let assetPrice = vault.assetPrice

      // If indexer doesn't have totalAssetsUSD or price is missing/zero but we have assets
      // Mark for oracle price fallback by setting assetPrice to null
      if ((totalAssetsUSD === 0 || assetPrice === null || assetPrice === 0) && totalAssets > 0n) {
        // totalAssetsUSD will be 0, indicating we need oracle price fallback
        totalAssetsUSD = 0
        assetPrice = null
      }

      vaultMap.set(normalizedAddress, {
        chainId,
        address: normalizedAddress,
        name: vault.vaultName ?? 'Unknown Vault',
        symbol: vault.vaultSymbol ?? 'UNKNOWN',
        decimals: vault.vaultDecimals ?? 18,
        asset: vault.asset ? (getAddress(vault.asset) as Address) : null,
        assetSymbol: vault.assetSymbol,
        assetDecimals,
        assetPrice,
        oracle: null, // Will be fetched on-demand by enrichment function
        unitOfAccount: null, // Will be fetched on-demand by enrichment function
        totalBorrows: BigInt(vault.totalBorrows || '0'),
        cash: BigInt(vault.cash || '0'),
        totalAssets,
        totalShares: BigInt(vault.totalShares || '0'),
        totalAssetsUSD,
        baseApy: vault.baseApy,
        totalApy: vault.totalApy,
        intrinsicApy: vault.intrinsicApy,
        supplyApy7d: vault.supplyApy7d ?? null,
        supplyApy30d: vault.supplyApy30d ?? null,
        supplyApy90d: vault.supplyApy90d ?? null,
        borrowApy7d: vault.borrowApy7d ?? null,
        borrowApy30d: vault.borrowApy30d ?? null,
        borrowApy90d: vault.borrowApy90d ?? null,
        utilization: vault.utilization,
        supplyCap: vault.supplyCap ? BigInt(vault.supplyCap) : null,
        governorAdmin: vault.governorAdmin ? (getAddress(vault.governorAdmin) as Address) : null,
        governorType: vault.governorType,
        perspectives: vault.perspectives?.map((addr) => getAddress(addr) as Address) ?? [],
        entities: vault.entities ?? [],
        products: vault.products ?? [],
        rewardsMetadata: vault.rewardsMetadata ?? [],
        exposure: vault.exposure ?? null,
        points: vault.points ?? [],
        source: 'indexer',
      })
    } catch {
      // Skip invalid addresses
      console.warn(`Invalid ELend vault address: ${vault.vault}`)
    }
  }

  return vaultMap
}

/**
 * Query options for fetching Euler Indexer ELends with perspective filtering
 */
export function eulerELendsQueryOptions(chainId: number = E_LEND_CHAIN_ID, perspectiveSettings?: PerspectiveSettings) {
  return queryOptions({
    queryKey: eulerELendsQueryKey(chainId, perspectiveSettings),
    queryFn: async () => {
      const vaults = await fetchEulerIndexerELends(chainId, perspectiveSettings)
      return normalizeELends(vaults, chainId)
    },
    staleTime: INDEXER_STALE_TIME,
    gcTime: INDEXER_GC_TIME,
    refetchInterval: INDEXER_REFETCH_INTERVAL,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  })
}

/**
 * Hook to fetch ELends from Euler Indexer with optional perspective filtering
 */
export function useEulerIndexerELends(chainId: number = E_LEND_CHAIN_ID, perspectiveSettings?: PerspectiveSettings) {
  return useQuery(eulerELendsQueryOptions(chainId, perspectiveSettings))
}

/**
 * Hook to fetch ELends with configurable perspective filtering
 * Uses SHOW_ALL_ELEND_VAULTS constant to determine if filtering should be applied
 * @param enabled - When false, skips fetching. Defaults to true.
 */
export function useEulerIndexerDefaultELends(chainId: number = E_LEND_CHAIN_ID, enabled = true) {
  // Always use perspective filters for the indexer query —
  // the indexer requires them to return results.
  // Dev mode injects missing product vaults via on-chain fallback in useDefaultELendsList.
  const perspectiveSettings = {
    enabledPerspectives: DEFAULT_ENABLED_PERSPECTIVES,
    disabledPerspectives: DEFAULT_DISABLED_PERSPECTIVES,
  }

  return useQuery({
    ...eulerELendsQueryOptions(chainId, perspectiveSettings),
    enabled,
  })
}

/**
 * Hook to fetch ELends filtered by the governed perspective only
 */
export function useEulerIndexerGovernedELends(chainId: number = E_LEND_CHAIN_ID) {
  return useEulerIndexerELends(chainId, {
    enabledPerspectives: [PERSPECTIVE_ADDRESSES.governed],
  })
}

/**
 * Hook to get a specific ELend vault by address from the cached vault list
 * Returns undefined if not found in cache
 */
export function useEulerIndexerELend(
  address: Address | undefined,
  chainId: number = E_LEND_CHAIN_ID,
  perspectiveSettings?: PerspectiveSettings,
) {
  const { data: vaultMap } = useEulerIndexerELends(chainId, perspectiveSettings)

  if (!address || !vaultMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return vaultMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Prefetch Euler Indexer ELends - useful for SSR or initial load
 */
export function usePrefetchEulerELends(chainId: number = E_LEND_CHAIN_ID, perspectiveSettings?: PerspectiveSettings) {
  const queryClient = useQueryClient()

  return () => {
    return queryClient.prefetchQuery(eulerELendsQueryOptions(chainId, perspectiveSettings))
  }
}

/**
 * Get ELend vault from cache synchronously (for use in callbacks)
 */
export function getELendFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  address: Address,
  perspectiveSettings?: PerspectiveSettings,
): NormalizedELendInfo | undefined {
  const vaultMap = queryClient.getQueryData<Map<Address, NormalizedELendInfo>>(
    eulerELendsQueryKey(chainId, perspectiveSettings),
  )

  if (!vaultMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return vaultMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Set ELend vault in cache - useful after onchain fallback fetch
 */
export function setELendInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  vault: NormalizedELendInfo,
  perspectiveSettings?: PerspectiveSettings,
): void {
  const queryKey = eulerELendsQueryKey(chainId, perspectiveSettings)
  const currentMap = queryClient.getQueryData<Map<Address, NormalizedELendInfo>>(queryKey)

  if (currentMap) {
    const newMap = new Map(currentMap)
    newMap.set(vault.address, vault)
    queryClient.setQueryData(queryKey, newMap)
  } else {
    // If no cache exists, create new map with this vault
    const newMap = new Map<Address, NormalizedELendInfo>()
    newMap.set(vault.address, vault)
    queryClient.setQueryData(queryKey, newMap)
  }
}
